import ListActionsMenu from "../documents/ListActionsMenu";
import {
  CButton,
  CButtonGroup,
  CDropdown,
  CDropdownItem,
  CDropdownToggle,
  CTooltip,
} from "@coreui/react";
import React from "react";
import {
  RECEIPT_STATUS_DRAFT,
  RECEIPT_STATUS_GENERATED,
  Receipt,
} from "../../models/receipt";
import PermissionRequiredComponent from "../permissions/PermissionRequiredComponent";
import {
  RECEIPTS_DESTROY,
  RECEIPTS_RETRIEVE,
  RECEIPTS_UPDATE,
} from "../../auth/permissions";

const MAX_VISIBLE_ACTIONS = 3;

interface ReceiptCrudAtionsProps {
  receipt: Receipt;
  onCancellationClick: (_: Receipt) => void;
  onReadClick: (_: Receipt) => void;
  onUpdateClick: (_: Receipt) => void;
  onDeleteClick: (_: Receipt) => void;
  onHistoryClick: (_: Receipt) => void;
  onConfirmDraftClick: (_: Receipt) => void;
  onDiscardDraftClick: (_: Receipt) => void;
  onSendEmailClick: (_: Receipt) => void;
  onRegenerateKudeClick: (_: Receipt) => void;
}

const ReceiptCrudActions: React.FC<ReceiptCrudAtionsProps> = ({
  receipt,
  onCancellationClick,
  onReadClick,
  onUpdateClick,
  onDeleteClick,
  onHistoryClick,
  onConfirmDraftClick,
  onDiscardDraftClick,
  onSendEmailClick,
  onRegenerateKudeClick,
}) => {
  const shouldShowDraftActions = receipt.status === RECEIPT_STATUS_DRAFT;
  const cancelButton = (
    <PermissionRequiredComponent permissionName={RECEIPTS_UPDATE}>
      <CTooltip content="Anular">
        <CButton
          className="text-white"
          color="secondary"
          onClick={() => {
            onCancellationClick(receipt);
          }}
        >
          <i className="fa fa-ban"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const editButton = (
    <PermissionRequiredComponent permissionName={RECEIPTS_UPDATE}>
      <CTooltip content="Editar">
        <CButton
          className="text-white"
          color="warning"
          onClick={() => {
            onUpdateClick(receipt);
          }}
        >
          <i className="fa fa-pencil"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const deleteButton = (
    <PermissionRequiredComponent permissionName={RECEIPTS_DESTROY}>
      <CTooltip content="Eliminar">
        <CButton
          className="text-white"
          color="danger"
          onClick={() => {
            onDeleteClick(receipt);
          }}
        >
          <i className="fa fa-trash"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const confirmDraftButton = (
    <PermissionRequiredComponent permissionName={RECEIPTS_UPDATE}>
      <CTooltip content="Confirmar Borrador">
        <CButton
          className="text-white"
          color="success"
          onClick={() => {
            onConfirmDraftClick(receipt);
          }}
        >
          <i className="fa fa-check"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const discardDraftButton = (
    <PermissionRequiredComponent permissionName={RECEIPTS_UPDATE}>
      <CTooltip content="Descartar Borrador">
        <CButton
          className="text-white"
          color="danger"
          onClick={() => {
            onDiscardDraftClick(receipt);
          }}
        >
          <i className="fa fa-times"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const shouldShowCancelButton = receipt.status === RECEIPT_STATUS_GENERATED;

  const shouldShowEditAndDeleteButton = !shouldShowDraftActions;

  const detailButton = (
    <PermissionRequiredComponent permissionName={RECEIPTS_RETRIEVE}>
      <CTooltip content="Ver Detalle">
        <CButton
          className="text-white"
          color="info"
          onClick={() => {
            onReadClick(receipt);
          }}
        >
          <i className="fa fa-eye"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const historyButton = (
    <PermissionRequiredComponent permissionName={RECEIPTS_RETRIEVE}>
      <CTooltip content="Ver Historial">
        <CButton
          className="text-white"
          color="dark"
          onClick={() => {
            onHistoryClick(receipt);
          }}
        >
          <i className="fa fa-history"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const sendEmailButton = (
    <PermissionRequiredComponent permissionName={RECEIPTS_UPDATE}>
      <CTooltip
        content={receipt.emailSent ? "Reenviar Correo" : "Enviar Correo"}
      >
        <CButton
          className="text-white"
          color="info"
          onClick={() => {
            onSendEmailClick(receipt);
          }}
        >
          <i className="fa fa-envelope"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const shouldShowSendEmailButton =
    receipt.status === RECEIPT_STATUS_GENERATED;

  const regenerateKudeButton = (
    <PermissionRequiredComponent permissionName={RECEIPTS_UPDATE}>
      <CTooltip content="Regenerar KUDE">
        <CButton
          className="text-white"
          color="secondary"
          onClick={() => {
            onRegenerateKudeClick(receipt);
          }}
        >
          <i className="fa fa-file-pdf"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const shouldShowRegenerateKudeButton =
    receipt.status === RECEIPT_STATUS_GENERATED;

  const allActions = [
    {
      element: detailButton,
      shouldShow: true,
      label: "Ver Detalle",
      icon: "fa-eye",
      permission: RECEIPTS_RETRIEVE,
      onClick: () => onReadClick(receipt),
    },
    {
      element: historyButton,
      shouldShow: !shouldShowDraftActions,
      label: "Ver Historial",
      icon: "fa-history",
      permission: RECEIPTS_RETRIEVE,
      onClick: () => onHistoryClick(receipt),
    },
    {
      element: confirmDraftButton,
      shouldShow: shouldShowDraftActions,
      label: "Confirmar Borrador",
      icon: "fa-check",
      permission: RECEIPTS_UPDATE,
      onClick: () => onConfirmDraftClick(receipt),
    },
    {
      element: discardDraftButton,
      shouldShow: shouldShowDraftActions,
      label: "Descartar Borrador",
      icon: "fa-times",
      permission: RECEIPTS_UPDATE,
      onClick: () => onDiscardDraftClick(receipt),
    },
    {
      element: cancelButton,
      shouldShow: shouldShowCancelButton && !shouldShowDraftActions,
      label: "Anular",
      icon: "fa-ban",
      permission: RECEIPTS_UPDATE,
      onClick: () => onCancellationClick(receipt),
    },
    {
      element: editButton,
      shouldShow: shouldShowDraftActions || shouldShowEditAndDeleteButton,
      label: "Editar",
      icon: "fa-pencil",
      permission: RECEIPTS_UPDATE,
      onClick: () => onUpdateClick(receipt),
    },
    {
      element: deleteButton,
      shouldShow: shouldShowEditAndDeleteButton,
      label: "Eliminar",
      icon: "fa-trash",
      permission: RECEIPTS_DESTROY,
      onClick: () => onDeleteClick(receipt),
    },
    {
      element: sendEmailButton,
      shouldShow: shouldShowSendEmailButton && !shouldShowDraftActions,
      label: receipt.emailSent ? "Reenviar Correo" : "Enviar Correo",
      icon: "fa-envelope",
      permission: RECEIPTS_UPDATE,
      onClick: () => onSendEmailClick(receipt),
    },
    {
      element: regenerateKudeButton,
      shouldShow: shouldShowRegenerateKudeButton && !shouldShowDraftActions,
      label: "Regenerar KUDE",
      icon: "fa-file-pdf",
      permission: RECEIPTS_UPDATE,
      onClick: () => onRegenerateKudeClick(receipt),
    },
  ];

  const visibleActions = allActions.filter((a) => a.shouldShow);
  const shouldCollapse = visibleActions.length > MAX_VISIBLE_ACTIONS;
  const primaryActions = shouldCollapse
    ? visibleActions.slice(0, MAX_VISIBLE_ACTIONS - 1)
    : visibleActions;
  const overflowActions = shouldCollapse
    ? visibleActions.slice(MAX_VISIBLE_ACTIONS - 1)
    : [];

  return (
    <td className="text-center">
      <CButtonGroup>
        {primaryActions.map((a, i) => (
          <React.Fragment key={i}>{a.element}</React.Fragment>
        ))}
        {shouldCollapse && (
          <CDropdown className="btn-group">
            <CTooltip content="Más opciones">
              <CDropdownToggle
                color="primary"
                className="text-white"
                caret={false}
                style={{ borderTopRightRadius: "0.25rem", borderBottomRightRadius: "0.25rem" }}
              >
                <i className="fa fa-ellipsis-v"></i>
              </CDropdownToggle>
            </CTooltip>
            <ListActionsMenu>
              {overflowActions.map((a, i) => (
                <PermissionRequiredComponent
                  key={i}
                  permissionName={a.permission}
                >
                  <CDropdownItem onClick={a.onClick}>
                    <i className={`fa ${a.icon} mr-2`}></i>
                    {a.label}
                  </CDropdownItem>
                </PermissionRequiredComponent>
              ))}
            </ListActionsMenu>
          </CDropdown>
        )}
      </CButtonGroup>
    </td>
  );
};

export default ReceiptCrudActions;
